import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Box,
  Cable,
  Database,
  Globe,
  Keyboard,
  Link2,
  Monitor,
  Search,
  Tv,
  Zap,
} from 'lucide-react'
import { apiDelete, apiGet, apiPatch, apiPost } from '../lib/api'
import { useTenantId } from '../context/SessionContext'
import { useToast } from '../context/ToastContext'
import { CreatePolicyModal } from '../components/CreatePolicyModal'
import { ResourceTagsEditor } from '../components/ResourceTagsEditor'
import { EffectiveAccessPanel } from '../components/EffectiveAccessPanel'
import { useResourceGroupsEnabled } from '../hooks/useResourceGroupsEnabled'
import {
  Badge,
  Button,
  Checkbox,
  DataTable,
  EmptyState,
  FormField,
  FormSection,
  Input,
  Label,
  Modal,
  PageHeader,
  Pagination,
  Select,
  Skeleton,
  Switch,
  THead,
  Th,
  Td,
  Tr,
} from '../components/ui'

const PAGE_SIZE = 25
// Detects the same IPv4-CIDR shape the backend's ListFilter.Query auto-detects
// (regexp in resource.go) — used only to show a "matching as CIDR" hint;
// the actual containment check always happens server-side.
const CIDR_RE = /^(\d{1,3}(?:\.\d{1,3}){3})\/(\d{1,2})$/
const IPV4_RE = /^\d{1,3}(?:\.\d{1,3}){3}$/

interface ConnectorPool {
  id: string
  name: string
  selection_policy: string
}

interface PagedResponse<T> {
  items: T[]
  page: number
  page_size: number
  total: number
}

interface Resource {
  id: string
  name: string
  description: string
  icon_url?: string
  icon_glyph?: string
  type: string
  deployment_type: string
  backend_url: string
  launch_url: string
  subdomain: string
  target_host: string
  target_port: number
  target_protocol: string
  connector_pool_id: string
  selection_policy: string
  is_visible: boolean
  clipboard_allowed?: boolean
  recording_enabled?: boolean
  rdp_domain?: string
  target_fqdn?: string
  access_modes?: string[]
  native_rdp_allowed?: boolean
}

const RESOURCE_TYPES = ['WEB', 'SSH', 'RDP', 'VNC', 'TCP', 'DATABASE', 'API']

// Mirrors the allow-list in saas/services/control-plane/internal/resource/validate.go
// (ValidateIconGlyph) and saas/db/migrations/015_resource_icon_glyph.sql — keep in sync.
const ICON_GLYPHS = [
  { value: 'server', label: 'Server' },
  { value: 'globe', label: 'Globe' },
  { value: 'plane', label: 'Plane' },
  { value: 'store', label: 'Store' },
  { value: 'users', label: 'Users' },
  { value: 'database', label: 'Database' },
  { value: 'terminal', label: 'Terminal' },
  { value: 'monitor', label: 'Monitor' },
  { value: 'eye', label: 'Eye' },
  { value: 'cloud', label: 'Cloud' },
  { value: 'zap', label: 'Zap' },
  { value: 'lock', label: 'Lock' },
]

const typeIcons: Record<string, ReactNode> = {
  SSH: <Keyboard className="h-4 w-4" />,
  RDP: <Monitor className="h-4 w-4" />,
  VNC: <Tv className="h-4 w-4" />,
  WEB: <Globe className="h-4 w-4" />,
  TCP: <Link2 className="h-4 w-4" />,
  DATABASE: <Database className="h-4 w-4" />,
  API: <Zap className="h-4 w-4" />,
}

export default function AdminResourcesPage() {
  const tenantId = useTenantId()
  const { enabled: resourceGroupsEnabled } = useResourceGroupsEnabled(tenantId)
  const [searchParams] = useSearchParams()
  const { toast } = useToast()
  const [pools, setPools] = useState<ConnectorPool[]>([])
  const [error, setError] = useState<string | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [editID, setEditID] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  // ---- search/filter/pagination — server-side, mirrors the Policies page's
  // ?q=/?type=/?deployment_type=/?connector_pool_id= + limit/offset contract ----
  const [query, setQuery] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const [deploymentFilter, setDeploymentFilter] = useState('')
  const [poolFilter, setPoolFilter] = useState('')
  const [tagFilter, setTagFilter] = useState(searchParams.get('tag_id') ?? '')
  const [tagOptions, setTagOptions] = useState<{ id: string; key: string; value: string }[]>([])
  const [bulkTagId, setBulkTagId] = useState('')
  const [bulkTagOpen, setBulkTagOpen] = useState(false)
  const [bulkTagRemove, setBulkTagRemove] = useState(false)
  const [bulkTagging, setBulkTagging] = useState(false)
  const [page, setPage] = useState(1)
  const [listResp, setListResp] = useState<PagedResponse<Resource>>({ items: [], page: 1, page_size: PAGE_SIZE, total: 0 })
  const [listLoading, setListLoading] = useState(true)

  // Debounce the free-text query so every keystroke doesn't fire a request.
  const [debouncedQuery, setDebouncedQuery] = useState('')
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 300)
    return () => clearTimeout(t)
  }, [query])

  // Per-resource policy count, fetched as cheap limit=1 requests (only reads
  // `total`) for whichever rows are on the current page — same pattern as
  // the Policies page's facet counts, scales with page size, not with the
  // tenant's total policy count.
  const [policyCounts, setPolicyCounts] = useState<Record<string, number>>({})

  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [addPolicyFor, setAddPolicyFor] = useState<Resource | null>(null)

  const emptyForm = () => ({
    name: '', description: '', icon_url: '', icon_glyph: '', type: 'WEB',
    deployment_type: 'connector',
    backend_url: '',
    launch_url: '',
    subdomain: '',
    target_host: '', target_port: 80, target_protocol: 'http',
    connector_pool_id: '', is_visible: true,
    clipboard_allowed: false, recording_enabled: false,
    rdp_domain: '', target_fqdn: '',
    access_modes: ['browser'] as string[],
    native_rdp_allowed: false,
  })
  const [form, setForm] = useState(emptyForm())

  useEffect(() => {
    if (!tenantId) return
    apiGet<ConnectorPool[]>(`/api/v1/tenants/${tenantId}/connector-pools`)
      .then((d) => setPools(d ?? []))
      .catch(() => {})
  }, [tenantId])

  useEffect(() => {
    if (!tenantId || !resourceGroupsEnabled) return
    apiGet<{ items: { id: string; key: string; value: string }[] }>(
      `/api/v1/tenants/${tenantId}/tags?limit=500`,
    )
      .then((d) => setTagOptions(d.items ?? []))
      .catch(() => setTagOptions([]))
  }, [tenantId, resourceGroupsEnabled])

  // Reset to page 1 whenever a filter/search actually changes the result set.
  useEffect(() => {
    setPage(1)
  }, [debouncedQuery, typeFilter, deploymentFilter, poolFilter, tagFilter])

  const loadResources = useCallback(() => {
    if (!tenantId) return
    setListLoading(true)
    setError(null)
    const params: Record<string, string | number> = { limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE }
    if (debouncedQuery) params.q = debouncedQuery
    if (typeFilter) params.type = typeFilter
    if (deploymentFilter) params.deployment_type = deploymentFilter
    if (poolFilter) params.connector_pool_id = poolFilter
    if (tagFilter) params.tag_id = tagFilter
    const qs = new URLSearchParams(params as Record<string, string>).toString()
    apiGet<PagedResponse<Resource>>(`/api/v1/tenants/${tenantId}/resources?${qs}`)
      .then(setListResp)
      .catch((e: any) => setError(e.message))
      .finally(() => setListLoading(false))
  }, [tenantId, page, debouncedQuery, typeFilter, deploymentFilter, poolFilter, tagFilter])

  useEffect(() => {
    loadResources()
  }, [loadResources])

  // Drop stale selections whenever the visible page changes underneath them.
  useEffect(() => {
    setSelected((prev) => {
      const validIds = new Set(listResp.items.map((r) => r.id))
      const next = new Set([...prev].filter((id) => validIds.has(id)))
      return next.size === prev.size ? prev : next
    })
  }, [listResp.items])

  useEffect(() => {
    if (!tenantId || listResp.items.length === 0) return
    Promise.all(
      listResp.items.map((r) =>
        apiGet<PagedResponse<{ id: string }>>(`/api/v1/tenants/${tenantId}/policies?resource_id=${r.id}&limit=1`)
          .then((resp) => [r.id, resp.total] as const)
          .catch(() => [r.id, 0] as const),
      ),
    ).then((entries) => setPolicyCounts((prev) => ({ ...prev, ...Object.fromEntries(entries) })))
  }, [tenantId, listResp.items])

  const cidrHint = useMemo(() => {
    const q = query.trim()
    if (!q) return null
    if (CIDR_RE.test(q)) return `Matching as CIDR: every resource whose target IP falls inside ${q}`
    if (IPV4_RE.test(q)) return `Matching "${q}" as plain text — add a /prefix (e.g. ${q}/24) for real subnet matching`
    return null
  }, [query])

  const openCreate = () => {
    setEditID(null)
    setForm(emptyForm())
    setShowForm(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    try {
      if (editID) {
        await apiPatch(`/api/v1/tenants/${tenantId}/resources/${editID}`, form)
      } else {
        await apiPost(`/api/v1/tenants/${tenantId}/resources`, form)
      }
      setForm(emptyForm())
      setShowForm(false)
      setEditID(null)
      toast({ title: editID ? 'Resource updated' : 'Resource created', variant: 'success' })
      loadResources()
    } catch (e: any) {
      setError(e.message)
      toast({ title: 'Save failed', description: e.message, variant: 'danger' })
    } finally {
      setSaving(false)
    }
  }

  const handleEdit = (r: Resource) => {
    setForm({
      name: r.name, description: r.description ?? '', icon_url: r.icon_url ?? '', icon_glyph: r.icon_glyph ?? '', type: r.type,
      deployment_type: r.deployment_type ?? 'connector',
      backend_url: r.backend_url ?? '',
      launch_url: r.launch_url ?? '',
      subdomain: r.subdomain ?? '',
      target_host: r.target_host, target_port: r.target_port, target_protocol: r.target_protocol,
      connector_pool_id: r.connector_pool_id ?? '', is_visible: r.is_visible,
      clipboard_allowed: r.clipboard_allowed ?? false,
      recording_enabled: r.recording_enabled ?? false,
      rdp_domain: r.rdp_domain ?? '',
      target_fqdn: r.target_fqdn ?? '',
      access_modes: r.access_modes ?? ['browser'],
      native_rdp_allowed: r.native_rdp_allowed ?? false,
    })
    setEditID(r.id)
    setShowForm(true)
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this resource?')) return
    try {
      await apiDelete(`/api/v1/tenants/${tenantId}/resources/${id}`)
      toast({ title: 'Resource deleted', variant: 'success' })
      loadResources()
    } catch (e: any) {
      setError(e.message)
      toast({ title: 'Delete failed', description: e.message, variant: 'danger' })
    }
  }

  const handleBulkDelete = async () => {
    const ids = [...selected]
    if (ids.length === 0) return
    if (!confirm(`Delete ${ids.length} selected resource${ids.length === 1 ? '' : 's'}? This cannot be undone.`)) return
    const results = await Promise.allSettled(ids.map((id) => apiDelete(`/api/v1/tenants/${tenantId}/resources/${id}`)))
    const failed = results.filter((r) => r.status === 'rejected').length
    if (failed > 0) {
      toast({ title: `${ids.length - failed} deleted, ${failed} failed`, variant: 'danger' })
    } else {
      toast({ title: `${ids.length} resource${ids.length === 1 ? '' : 's'} deleted`, variant: 'success' })
    }
    setSelected(new Set())
    loadResources()
  }

  const handleBulkTag = async () => {
    const ids = [...selected]
    if (ids.length === 0 || !bulkTagId) return
    setBulkTagging(true)
    try {
      await apiPost(`/api/v1/tenants/${tenantId}/resources/tags/bulk`, {
        resource_ids: ids,
        tag_id: bulkTagId,
        remove: bulkTagRemove,
      })
      toast({ title: bulkTagRemove ? 'Tags removed' : 'Tags assigned', variant: 'success' })
      setBulkTagOpen(false)
      setSelected(new Set())
      loadResources()
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Bulk tag failed'
      toast({ title: 'Bulk tag failed', description: msg, variant: 'danger' })
    } finally {
      setBulkTagging(false)
    }
  }

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleSelectAll = () => {
    setSelected((prev) => {
      const allSelected = listResp.items.length > 0 && listResp.items.every((r) => prev.has(r.id))
      return allSelected ? new Set() : new Set(listResp.items.map((r) => r.id))
    })
  }

  const poolName = (pid: string) => pools.find((p) => p.id === pid)?.name

  return (
    <div>
      <PageHeader
        title="Resources"
        description="Manage the resources available through Idefy and how they are accessed."
        actions={
          <Button onClick={openCreate}>+ New Resource</Button>
        }
      />

      {error && (
        <div className="mb-4 rounded-lg border border-danger/30 bg-danger-muted px-4 py-3 text-sm text-danger-foreground">
          {error}
        </div>
      )}

      <div className="mb-1 flex flex-wrap items-center gap-3">
        <div className="relative min-w-[240px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
          <Input
            className="pl-9"
            placeholder="Search by name, target, IP, or CIDR (e.g. 10.2.0.0/24)…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <Select className="w-auto" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
          <option value="">All types</option>
          {RESOURCE_TYPES.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </Select>
        <Select className="w-auto" value={deploymentFilter} onChange={(e) => setDeploymentFilter(e.target.value)}>
          <option value="">All deployments</option>
          <option value="connector">Private</option>
          <option value="reverse_proxy">Public</option>
          <option value="external_launch">External Launch</option>
        </Select>
        <Select className="w-auto" value={poolFilter} onChange={(e) => setPoolFilter(e.target.value)}>
          <option value="">All pools</option>
          {pools.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </Select>
        {resourceGroupsEnabled && (
          <Select className="w-auto" value={tagFilter} onChange={(e) => setTagFilter(e.target.value)}>
            <option value="">All tags</option>
            {tagOptions.map((t) => (
              <option key={t.id} value={t.id}>{t.key}: {t.value}</option>
            ))}
          </Select>
        )}
      </div>
      {cidrHint && <p className="mb-3 text-xs text-muted">{cidrHint}</p>}
      {!cidrHint && <div className="mb-3" />}

      {selected.size > 0 && (
        <div className="mb-3 flex items-center justify-between gap-3 rounded-lg bg-primary/10 px-3.5 py-2 text-sm text-primary">
          <span>{selected.size} selected</span>
          <div className="flex gap-2">
            {resourceGroupsEnabled && (
              <>
                <Button variant="outline" size="sm" onClick={() => { setBulkTagRemove(false); setBulkTagOpen(true) }}>
                  Assign tag
                </Button>
                <Button variant="outline" size="sm" onClick={() => { setBulkTagRemove(true); setBulkTagOpen(true) }}>
                  Remove tag
                </Button>
              </>
            )}
            <Button variant="outline" size="sm" className="border-danger/40 text-danger" onClick={handleBulkDelete}>
              Delete
            </Button>
          </div>
        </div>
      )}

      {listLoading ? (
        <Skeleton className="h-48 w-full" />
      ) : listResp.items.length === 0 ? (
        <EmptyState
          icon={<Box className="h-8 w-8" />}
          title={query || typeFilter || deploymentFilter || poolFilter ? 'No resources match your search or filters.' : 'No resources yet'}
          description="Create one to add it to the catalog."
          actionLabel="New Resource"
          onAction={openCreate}
        />
      ) : (
        <>
          <DataTable>
            <THead>
              <Tr>
                <Th className="w-8">
                  <Checkbox
                    checked={listResp.items.length > 0 && listResp.items.every((r) => selected.has(r.id))}
                    onChange={toggleSelectAll}
                  />
                </Th>
                <Th>Name</Th>
                <Th>Target</Th>
                <Th>Deployment</Th>
                <Th>Connector Pool</Th>
                <Th className="text-right">Actions</Th>
              </Tr>
            </THead>
            <tbody>
              {listResp.items.map((r) => (
                <Tr key={r.id}>
                  <Td>
                    <Checkbox checked={selected.has(r.id)} onChange={() => toggleSelect(r.id)} />
                  </Td>
                  <Td>
                    <div className="flex items-start gap-3">
                      <span className="mt-0.5 text-muted">{typeIcons[r.type] ?? <Box className="h-4 w-4" />}</span>
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-medium">{r.name}</p>
                          <Badge variant="default">{r.type}</Badge>
                          {!r.is_visible && <Badge variant="default">hidden</Badge>}
                        </div>
                        <div className="mt-1">
                          {policyCounts[r.id] === undefined ? null : policyCounts[r.id] === 0 ? (
                            <div className="flex items-center gap-1.5">
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-6 border-primary/40 px-2 text-xs text-primary"
                                onClick={() => setAddPolicyFor(r)}
                              >
                                + Add Policy
                              </Button>
                              <span
                                className="rounded-full bg-warning-muted px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-warning-foreground"
                                title="No policy grants access to this resource yet — network reachability is unaffected"
                              >
                                No policy
                              </span>
                            </div>
                          ) : (
                            <span className="text-xs text-primary">
                              {policyCounts[r.id]} {policyCounts[r.id] === 1 ? 'policy' : 'policies'}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </Td>
                  <Td className="font-mono text-xs text-muted">
                    {r.deployment_type === 'reverse_proxy' ? (
                      r.backend_url
                    ) : r.deployment_type === 'external_launch' ? (
                      r.launch_url
                    ) : r.backend_url ? (
                      r.backend_url
                    ) : (
                      `${r.target_protocol}://${r.target_host}:${r.target_port}`
                    )}
                    {r.subdomain && <p className="mt-0.5 text-primary">Subdomain: {r.subdomain}</p>}
                  </Td>
                  <Td>
                    {r.deployment_type === 'reverse_proxy' ? (
                      <Badge variant="public">Public</Badge>
                    ) : r.deployment_type === 'external_launch' ? (
                      <Badge variant="default" title="Idefy authorizes launch only — no proxying or federation">
                        External Launch
                      </Badge>
                    ) : (
                      <Badge
                        variant="private"
                        title={
                          r.connector_pool_id
                            ? `Routed via ${poolName(r.connector_pool_id) ?? r.connector_pool_id}`
                            : 'No connector pool assigned'
                        }
                      >
                        Private
                      </Badge>
                    )}
                  </Td>
                  <Td className="text-sm">
                    {r.connector_pool_id ? (
                      <div>
                        <span className="text-primary">{poolName(r.connector_pool_id) ?? r.connector_pool_id}</span>
                        {r.selection_policy && <p className="text-xs text-muted">{r.selection_policy}</p>}
                      </div>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </Td>
                  <Td className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(r)}>
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-danger"
                        onClick={() => handleDelete(r.id)}
                      >
                        Delete
                      </Button>
                    </div>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </DataTable>
          {listResp.total > PAGE_SIZE && (
            <div className="mt-3">
              <Pagination page={listResp.page} pageSize={PAGE_SIZE} total={listResp.total} onPageChange={setPage} />
            </div>
          )}
        </>
      )}

      <CreatePolicyModal
        tenantId={tenantId}
        resources={listResp.items}
        open={!!addPolicyFor}
        onClose={() => setAddPolicyFor(null)}
        lockResourceId={addPolicyFor?.id}
        onSaved={(resourceId) => {
          apiGet<PagedResponse<{ id: string }>>(`/api/v1/tenants/${tenantId}/policies?resource_id=${resourceId}&limit=1`)
            .then((resp) => setPolicyCounts((prev) => ({ ...prev, [resourceId]: resp.total })))
            .catch(() => {})
        }}
      />

      <Modal
        open={showForm}
        onClose={() => { setShowForm(false); setEditID(null) }}
        title={editID ? 'Edit Resource' : 'Create Resource'}
        className="max-w-2xl"
        footer={
          <>
            <Button variant="outline" onClick={() => { setShowForm(false); setEditID(null) }}>
              Cancel
            </Button>
            <Button type="submit" form="resource-form" disabled={saving}>
              {saving ? 'Saving…' : editID ? 'Save Changes' : 'Create Resource'}
            </Button>
          </>
        }
      >
        <form id="resource-form" onSubmit={handleSubmit} className="space-y-6">
          <FormSection title="Identity" description="Catalog name and how users find this resource.">
            <div className="grid grid-cols-2 gap-4">
              <FormField label="Name *" htmlFor="res-name">
                <Input
                  id="res-name"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. HR Portal"
                />
              </FormField>
              <FormField
                label="Type"
                htmlFor="res-type"
                hint={form.deployment_type === 'external_launch' ? 'External Launch is WEB-only.' : undefined}
              >
                <Select
                  id="res-type"
                  value={form.type}
                  disabled={form.deployment_type === 'external_launch'}
                  onChange={(e) => setForm({ ...form, type: e.target.value })}
                >
                  {RESOURCE_TYPES.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </Select>
              </FormField>
            </div>
            <FormField label="Description" htmlFor="res-desc">
              <Input
                id="res-desc"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </FormField>
            <div className="grid grid-cols-2 gap-4">
              <FormField
                label="Icon URL"
                htmlFor="res-icon-url"
                hint="Optional. A real logo image for the catalog tile."
              >
                <Input
                  id="res-icon-url"
                  type="url"
                  value={form.icon_url}
                  onChange={(e) => setForm({ ...form, icon_url: e.target.value })}
                  placeholder="https://example.com/logo.png"
                />
              </FormField>
              <FormField
                label="Icon"
                htmlFor="res-icon-glyph"
                hint="Used when there's no Icon URL (or it fails to load)."
              >
                <Select
                  id="res-icon-glyph"
                  value={form.icon_glyph}
                  onChange={(e) => setForm({ ...form, icon_glyph: e.target.value })}
                >
                  <option value="">Default (based on type)</option>
                  {ICON_GLYPHS.map(({ value, label }) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </Select>
              </FormField>
            </div>
            {form.deployment_type !== 'external_launch' && (
              <FormField
                label="Portal Subdomain"
                htmlFor="res-subdomain"
                hint="Optional. Enables clean URLs like hrdirectory.apps.yourdomain.com instead of /app/<uuid>."
              >
                <Input
                  id="res-subdomain"
                  value={form.subdomain}
                  onChange={(e) => setForm({ ...form, subdomain: e.target.value.toLowerCase() })}
                  placeholder="e.g. hrdirectory → hrdirectory.apps.yourdomain.com"
                />
              </FormField>
            )}
          </FormSection>

          <FormSection title="Target" description="Where traffic is sent and how it is reached.">
            <div>
              <Label className="mb-2 block">Deployment Type</Label>
              <div className="flex gap-4">
                {[
                  { value: 'connector', label: 'Connector', desc: 'Private network via QUIC tunnel', icon: <Cable className="h-4 w-4" /> },
                  { value: 'reverse_proxy', label: 'Reverse Proxy', desc: 'Caddy proxies directly to a public backend URL', icon: <Globe className="h-4 w-4" /> },
                  { value: 'external_launch', label: 'External Launch', desc: 'Redirect to a SaaS app’s own SSO login (e.g. Salesforce)', icon: <Link2 className="h-4 w-4" /> },
                ].map(({ value, label, desc, icon }) => (
                  <label
                    key={value}
                    className={`flex flex-1 cursor-pointer items-start gap-2 rounded-lg border p-3 ${
                      form.deployment_type === value
                        ? 'border-primary bg-primary/5'
                        : 'border-border bg-surface'
                    }`}
                  >
                    <input
                      type="radio"
                      name="deployment_type"
                      value={value}
                      checked={form.deployment_type === value}
                      onChange={() => setForm({
                        ...form,
                        deployment_type: value,
                        backend_url: '',
                        connector_pool_id: '',
                        launch_url: '',
                        // external_launch doesn't support a portal subdomain
                        // yet (ADR-012 §1) — clear it going in; leave it
                        // alone for connector/reverse_proxy switches.
                        subdomain: value === 'external_launch' ? '' : form.subdomain,
                        // external_launch is WEB-only (ADR-012 §1).
                        type: value === 'external_launch' ? 'WEB' : form.type,
                      })}
                      className="mt-1"
                    />
                    <span>
                      <span className="flex items-center gap-1.5 text-sm font-medium text-foreground">
                        {icon} {label}
                      </span>
                      <span className="mt-0.5 block text-xs text-muted">{desc}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {form.deployment_type === 'reverse_proxy' && (
              <FormField
                label="Backend URL *"
                htmlFor="res-backend"
                hint="Must be a publicly routable HTTP/HTTPS URL. Private (RFC1918) addresses are rejected."
              >
                <Input
                  id="res-backend"
                  required
                  value={form.backend_url}
                  onChange={(e) => setForm({ ...form, backend_url: e.target.value })}
                  placeholder="https://app.example.com or http://203.0.113.10:8080"
                />
              </FormField>
            )}

            {form.deployment_type === 'external_launch' && (
              <FormField
                label="Launch URL *"
                htmlFor="res-launch"
                hint="The SaaS app's own entry point (e.g. its SSO/My Domain login URL). Idefy authorizes and redirects here — it does not proxy or federate into the app itself."
              >
                <Input
                  id="res-launch"
                  required
                  value={form.launch_url}
                  onChange={(e) => setForm({ ...form, launch_url: e.target.value })}
                  placeholder="https://acme.my.salesforce.com"
                />
              </FormField>
            )}

            {form.deployment_type === 'connector' && (
              <>
                <div className="grid grid-cols-3 gap-4">
                  <FormField
                    label="Target Host *"
                    htmlFor="res-host"
                    className="col-span-2"
                    hint="Bare host or IP only — no scheme, no path. For a WEB backend that needs a subpath (e.g. /RDWeb/webclient/), use Backend URL below instead."
                  >
                    <Input
                      id="res-host"
                      required={form.deployment_type === 'connector'}
                      value={form.target_host}
                      onChange={(e) => setForm({ ...form, target_host: e.target.value })}
                      placeholder="192.168.10.5 or app.internal"
                    />
                  </FormField>
                  <FormField label="Port *" htmlFor="res-port">
                    <Input
                      id="res-port"
                      required={form.deployment_type === 'connector'}
                      type="number"
                      min={1}
                      max={65535}
                      value={form.target_port}
                      onChange={(e) => setForm({ ...form, target_port: Number(e.target.value) })}
                    />
                  </FormField>
                </div>
                {form.type === 'WEB' && (
                  <FormField
                    label="Backend URL (optional)"
                    htmlFor="res-connector-backend-url"
                    hint="Only needed if the backend requires a specific subpath (e.g. https://10.0.0.7/RDWeb/webclient/). Overrides Target Host/Port/Protocol above when set; leave blank for a plain host:port private HTTP(S) target."
                  >
                    <Input
                      id="res-connector-backend-url"
                      value={form.backend_url}
                      onChange={(e) => setForm({ ...form, backend_url: e.target.value })}
                      placeholder="https://10.0.0.7/RDWeb/webclient/"
                    />
                  </FormField>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <FormField label="Protocol" htmlFor="res-protocol">
                    <Select
                      id="res-protocol"
                      value={form.target_protocol}
                      onChange={(e) => setForm({ ...form, target_protocol: e.target.value })}
                    >
                      <option value="http">HTTP</option>
                      <option value="https">HTTPS</option>
                      <option value="ssh">SSH</option>
                      <option value="rdp">RDP</option>
                      <option value="vnc">VNC</option>
                      <option value="tcp">TCP</option>
                    </Select>
                  </FormField>
                  <FormField label="Connector Pool" htmlFor="res-pool">
                    <Select
                      id="res-pool"
                      value={form.connector_pool_id}
                      onChange={(e) => setForm({ ...form, connector_pool_id: e.target.value })}
                    >
                      <option value="">— none —</option>
                      {pools.map((p) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </Select>
                  </FormField>
                </div>
              </>
            )}
          </FormSection>

          <FormSection title="Access" description="Visibility and protocol-specific access options.">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-foreground">Visible in user catalog</p>
                <p className="text-xs text-muted">Hide to keep the resource out of the portal catalog.</p>
              </div>
              <Switch
                checked={form.is_visible}
                onCheckedChange={(checked) => setForm({ ...form, is_visible: checked })}
              />
            </div>

            {form.type === 'RDP' && (
              <div className="space-y-4 rounded-lg border border-border p-4">
                <p className="text-xs font-semibold text-foreground">RDP (ADR-008)</p>
                <div className="grid grid-cols-2 gap-4">
                  <FormField label="RDP Domain" htmlFor="res-rdp-domain">
                    <Input
                      id="res-rdp-domain"
                      value={form.rdp_domain}
                      onChange={(e) => setForm({ ...form, rdp_domain: e.target.value })}
                      placeholder="CONTOSO"
                    />
                  </FormField>
                  <FormField label="Target FQDN" htmlFor="res-fqdn">
                    <Input
                      id="res-fqdn"
                      value={form.target_fqdn}
                      onChange={(e) => setForm({ ...form, target_fqdn: e.target.value })}
                      placeholder="vm01.corp.example.com"
                    />
                  </FormField>
                </div>
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-medium text-foreground">Allow native mstsc launch</p>
                    <p className="text-xs text-muted">Disabled when server-side recording is on.</p>
                  </div>
                  <Switch
                    checked={form.native_rdp_allowed}
                    disabled={form.recording_enabled}
                    onCheckedChange={(allowed) => {
                      const modes = new Set(form.access_modes ?? ['browser'])
                      if (allowed) {
                        modes.add('native')
                      } else {
                        modes.delete('native')
                      }
                      setForm({
                        ...form,
                        native_rdp_allowed: allowed,
                        access_modes: [...modes],
                      })
                    }}
                  />
                </div>
              </div>
            )}
          </FormSection>

          <FormSection title="Governance" description="Clipboard and session recording controls.">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-foreground">Clipboard allowed</p>
                <p className="text-xs text-muted">Permit clipboard sync during the session.</p>
              </div>
              <Switch
                checked={form.clipboard_allowed}
                onCheckedChange={(checked) => setForm({ ...form, clipboard_allowed: checked })}
              />
            </div>
            {form.type === 'RDP' && (
              <>
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-medium text-foreground">Server-side recording</p>
                    <p className="text-xs text-muted">Browser-only. Disables native mstsc when enabled.</p>
                  </div>
                  <Switch
                    checked={form.recording_enabled}
                    onCheckedChange={(recording) => {
                      const modes = new Set(form.access_modes ?? ['browser'])
                      if (recording) {
                        modes.delete('native')
                      }
                      setForm({
                        ...form,
                        recording_enabled: recording,
                        native_rdp_allowed: recording ? false : form.native_rdp_allowed,
                        access_modes: [...modes],
                      })
                    }}
                  />
                </div>
                <p className="text-xs text-muted">Browser engine: IronRDP sidecar only (Apache-2.0).</p>
              </>
            )}
          </FormSection>

          {editID && resourceGroupsEnabled && (
            <FormSection title="Tags">
              <ResourceTagsEditor tenantId={tenantId} resourceId={editID} enabled={resourceGroupsEnabled} />
            </FormSection>
          )}

          {editID && (
            <FormSection title="Access">
              <EffectiveAccessPanel tenantId={tenantId} resourceId={editID} enabled={true} />
            </FormSection>
          )}
        </form>
      </Modal>

      {bulkTagOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setBulkTagOpen(false)}>
          <div className="w-full max-w-sm rounded-lg border border-border bg-surface p-4 shadow-lg" onClick={(e) => e.stopPropagation()}>
            <p className="mb-2 text-sm font-medium">
              {bulkTagRemove ? 'Remove tag from selected resources' : 'Assign tag to selected resources'}
            </p>
            <p className="mb-3 text-xs text-muted">{selected.size} selected</p>
            <Select value={bulkTagId} onChange={(e) => setBulkTagId(e.target.value)} className="mb-3 w-full">
              <option value="">Select tag</option>
              {tagOptions.map((t) => (
                <option key={t.id} value={t.id}>{t.key}: {t.value}</option>
              ))}
            </Select>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setBulkTagOpen(false)}>Cancel</Button>
              <Button size="sm" disabled={!bulkTagId || bulkTagging} onClick={handleBulkTag}>
                {bulkTagging ? 'Working…' : bulkTagRemove ? 'Remove tag' : 'Assign tag'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
